import he from 'he';
import Head from 'next/head';
import { StaticImageData } from 'next/image';
import React from 'react';
import { useIntl } from 'react-intl';

import homepageThumbnail from '@/assets/home/homepage-social-media-thumbnail.jpg';

interface IHeadMetaProps {
  title?: string;
  description?: string;
  url?: string;
  canonicalUrl?: string;
  noindex?: boolean;
  nofollow?: boolean;
  thumbnailURL?: string | StaticImageData;
  hideOutdoorsyTitle?: boolean;
}

const HeadMeta: React.FC<React.PropsWithChildren<IHeadMetaProps>> = ({
  title: preferredTitle,
  description: preferredDescription,
  url,
  canonicalUrl,
  noindex,
  nofollow,
  children,
  thumbnailURL: preferredThumbnail,
  hideOutdoorsyTitle,
}) => {
  const intl = useIntl();

  const defaultTitle = intl.formatMessage({
    defaultMessage: 'RV Rentals: Explore the Open Road in Comfort',
    id: 'LQBwbZ',
  });

  const defaultDescription = intl.formatMessage({
    defaultMessage:
      'Find RV Rentals Near you on Outdoorsy. The only person-to-person RV rental marketplace in the US',
    id: 'qz1auY',
  });

  const defaultThumbnail = homepageThumbnail;

  const title = preferredTitle || defaultTitle;
  const description = preferredDescription || defaultDescription;
  const thumbnail = preferredThumbnail || defaultThumbnail;
  const thumbnailSrc = typeof thumbnail !== 'string' ? thumbnail.src : thumbnail;
  // StaticImageData carries width/height at build time; string URLs don't, so
  // we only emit og:image:width / og:image:height when we have a static import.
  const thumbnailWidth = typeof thumbnail !== 'string' ? thumbnail.width : undefined;
  const thumbnailHeight = typeof thumbnail !== 'string' ? thumbnail.height : undefined;

  // OG locale convention: en-US → en_US. Defaults to en_US if intl locale is unset.
  const ogLocale = (intl.locale || 'en-US').replace('-', '_');

  const ogUrl = url || canonicalUrl;

  let robotsContent =
    'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1';
  if (noindex && nofollow) {
    robotsContent = 'none';
  } else if (noindex) {
    robotsContent = 'noindex';
  }

  let computedTitle = 'Outdoorsy';
  if (title) {
    if (hideOutdoorsyTitle) {
      // eslint-disable-next-line import/no-named-as-default-member
      computedTitle = `${he.decode(title)}`;
    } else {
      // eslint-disable-next-line import/no-named-as-default-member
      computedTitle = `${he.decode(title)} | Outdoorsy`;
    }
  }

  return (
    <Head>
      <title>{computedTitle}</title>
      <meta name="description" content={description} />
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
      <meta property="fb:app_id" content="604465049688341" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {ogUrl && <meta property="og:url" content={ogUrl} />}
      <meta property="og:site_name" content="Outdoorsy" />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:image" content={thumbnailSrc} />
      {thumbnailWidth !== undefined && (
        <meta property="og:image:width" content={String(thumbnailWidth)} />
      )}
      {thumbnailHeight !== undefined && (
        <meta property="og:image:height" content={String(thumbnailHeight)} />
      )}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@outdoorsy" />
      <meta name="twitter:creator" content="@outdoorsy" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={thumbnailSrc} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      <meta name="robots" content={robotsContent} />
      {children}
    </Head>
  );
};

export default HeadMeta;
